(() => {
  const SELECTORS = {
    section: "[data-br-swiper-section]",
    carousel: "[data-br-swiper-carousel]",
    wrapper: ".br-swiper__wrapper",
    item: ".br-swiper__item",
    pagination: "[data-br-swiper-pagination]",
  };

  const DESKTOP_QUERY = "(min-width: 1000px)";

  function formatNumber(index) {
    return String(index + 1);
  }

  function getGroupSize() {
    return window.matchMedia(DESKTOP_QUERY).matches ? 2 : 1;
  }

  function getCssLengthPx(context, value, fallback = 16) {
    const probe = document.createElement("div");

    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.pointerEvents = "none";
    probe.style.width = value;

    context.appendChild(probe);

    const width = probe.getBoundingClientRect().width;

    probe.remove();

    return Number.isFinite(width) && width > 0 ? width : fallback;
  }

  function getGutterPx(section) {
    const width = getCssLengthPx(section, "var(--br-swiper-gutter)", 16);

    return width > 0 && width < 200 ? width : 16;
  }

  function getOffsetBefore(section) {
    return window.matchMedia(DESKTOP_QUERY).matches ? getGutterPx(section) : 0;
  }

  function buildSlides(section) {
    const wrapper = section.querySelector(SELECTORS.wrapper);
    if (!wrapper) return;

    const items = Array.from(wrapper.querySelectorAll(SELECTORS.item));
    const groupSize = getGroupSize();
    const fragment = document.createDocumentFragment();

    wrapper.innerHTML = "";

    items.forEach((item, index) => {
      if (index % groupSize === 0) {
        const slide = document.createElement("div");

        slide.className = "br-swiper__slide swiper-slide";
        fragment.appendChild(slide);
      }

      fragment.lastElementChild.appendChild(item);
    });

    wrapper.appendChild(fragment);
    section.brSwiperGroupSize = groupSize;
  }

  function destroySwiper(section) {
    destroyVideoObserver(section);

    if (section.brSwiperMediaQuery && section.brSwiperMediaQueryHandler) {
      if (section.brSwiperMediaQuery.removeEventListener) {
        section.brSwiperMediaQuery.removeEventListener(
          "change",
          section.brSwiperMediaQueryHandler,
        );
      } else if (section.brSwiperMediaQuery.removeListener) {
        section.brSwiperMediaQuery.removeListener(
          section.brSwiperMediaQueryHandler,
        );
      }

      section.brSwiperMediaQuery = null;
      section.brSwiperMediaQueryHandler = null;
    }

    if (section.brSwiper && typeof section.brSwiper.destroy === "function") {
      section.brSwiper.destroy(true, true);
      section.brSwiper = null;
    }
  }

  function updateSwiperMetrics(section, swiper) {
    if (!swiper) return;

    const gutter = getGutterPx(section);

    swiper.params.spaceBetween = gutter;
    swiper.params.slidesOffsetBefore = getOffsetBefore(section);
    swiper.params.slidesOffsetAfter = gutter;

    swiper.update();
  }

  function renderPagination(section, swiper) {
    const pagination = section.querySelector(SELECTORS.pagination);
    if (!pagination || !swiper) return;

    pagination.innerHTML = "";

    swiper.slides.forEach((slide, index) => {
      const bullet = document.createElement("button");

      bullet.type = "button";
      bullet.className = "br-swiper__bullet";
      bullet.textContent = formatNumber(index);
      bullet.setAttribute("aria-label", `Go to slide ${index + 1}`);

      bullet.addEventListener("click", () => {
        preloadSlideImages(swiper, index);
        preloadSlideImages(swiper, index + 1);
        swiper.slideTo(index);
      });

      pagination.appendChild(bullet);
    });
  }

  function updatePagination(section, swiper) {
    const bullets = section.querySelectorAll(".br-swiper__bullet");
    if (!bullets.length || !swiper) return;

    let activeIndex = swiper.activeIndex;

    if (swiper.isBeginning) {
      activeIndex = 0;
    }

    if (swiper.isEnd) {
      activeIndex = bullets.length - 1;
    }

    bullets.forEach((bullet, index) => {
      const isActive = index === activeIndex;

      bullet.classList.toggle("is-active", isActive);
      bullet.setAttribute("aria-current", isActive ? "true" : "false");
    });
  }

  function preloadSlideImages(swiper, index) {
    const slide = swiper?.slides?.[index];

    if (!slide) return;

    slide.querySelectorAll(".br-swiper__image").forEach((image) => {
      image.loading = "eager";
      image.fetchPriority = "auto";

      if (typeof image.decode === "function" && !image.complete) {
        image.decode().catch(() => {});
      }
    });
  }

  function preloadNearbyImages(swiper) {
    if (!swiper) return;

    const activeIndex = swiper.activeIndex;

    preloadSlideImages(swiper, activeIndex - 1);
    preloadSlideImages(swiper, activeIndex);
    preloadSlideImages(swiper, activeIndex + 1);
    preloadSlideImages(swiper, activeIndex + 2);
  }

  function playVideo(video) {
    if (!video || !video.paused) return;

    const playPromise = video.play();

    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }

  function pauseVideo(video) {
    if (!video || video.paused) return;

    video.pause();
  }

  function updateVideoPlayback(section) {
    const videos = section.querySelectorAll(".br-swiper__video");

    videos.forEach((video) => {
      const shouldPlay =
        video.dataset.brSwiperVideoVisible === "true" && !document.hidden;

      if (shouldPlay) {
        playVideo(video);
      } else {
        pauseVideo(video);
      }
    });
  }

  function setupVideoObserver(section) {
    const videos = section.querySelectorAll(".br-swiper__video");

    if (!videos.length || !("IntersectionObserver" in window)) return;

    section.brSwiperVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.dataset.brSwiperVideoVisible =
            entry.isIntersecting && entry.intersectionRatio >= 0.45
              ? "true"
              : "false";
        });

        updateVideoPlayback(section);
      },
      {
        threshold: [0, 0.45],
      },
    );

    videos.forEach((video) => {
      video.dataset.brSwiperVideoVisible = "false";
      section.brSwiperVideoObserver.observe(video);
    });

    updateVideoPlayback(section);
  }

  function destroyVideoObserver(section) {
    if (section.brSwiperVideoObserver) {
      section.brSwiperVideoObserver.disconnect();
      section.brSwiperVideoObserver = null;
    }

    section.querySelectorAll(".br-swiper__video").forEach((video) => {
      pauseVideo(video);
      delete video.dataset.brSwiperVideoVisible;
    });
  }

  function initSwiper(section) {
    if (!window.Swiper || !section) return;

    const carousel = section.querySelector(SELECTORS.carousel);

    if (!carousel || carousel.classList.contains("swiper-initialized")) return;

    buildSlides(section);

    section.brSwiper = new Swiper(carousel, {
      slidesPerView: "auto",
      slidesPerGroup: 1,
      spaceBetween: getGutterPx(section),
      slidesOffsetBefore: getOffsetBefore(section),
      slidesOffsetAfter: getGutterPx(section),
      speed: 780,
      loop: false,
      rewind: false,
      grabCursor: false,
      simulateTouch: false,
      allowTouchMove: true,
      threshold: 8,
      preventClicks: true,
      preventClicksPropagation: true,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
        sensitivity: 1,
        thresholdDelta: 10,
        thresholdTime: 120,
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      a11y: {
        enabled: true,
        prevSlideMessage: "Previous slide",
        nextSlideMessage: "Next slide",
      },
      on: {
        init(swiper) {
          updateSwiperMetrics(section, swiper);
          renderPagination(section, swiper);
          updatePagination(section, swiper);
          preloadNearbyImages(swiper);
          setupVideoObserver(section);
        },
        slideChange(swiper) {
          updatePagination(section, swiper);
          preloadNearbyImages(swiper);
          updateVideoPlayback(section);
        },
        resize(swiper) {
          updateSwiperMetrics(section, swiper);
          updatePagination(section, swiper);
          preloadNearbyImages(swiper);
        },
      },
    });

    section.brSwiperMediaQuery = window.matchMedia(DESKTOP_QUERY);
    section.brSwiperMediaQueryHandler = () => {
      destroySwiper(section);
      initSwiper(section);
    };

    if (section.brSwiperMediaQuery.addEventListener) {
      section.brSwiperMediaQuery.addEventListener(
        "change",
        section.brSwiperMediaQueryHandler,
      );
    } else if (section.brSwiperMediaQuery.addListener) {
      section.brSwiperMediaQuery.addListener(section.brSwiperMediaQueryHandler);
    }
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.section).forEach(initSwiper);
  }

  document.addEventListener("visibilitychange", () => {
    document.querySelectorAll(SELECTORS.section).forEach(updateVideoPlayback);
  });

  document.addEventListener("DOMContentLoaded", initAll);
  window.addEventListener("load", initAll);

  document.addEventListener("shopify:section:load", (event) => {
    initSwiper(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroySwiper(event.target);
  });
})();
